/**
 * Artist partner landing: scroll reveals, micro-animations, Spotify modal.
 */
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function applyStaggerDelays(root) {
  root.querySelectorAll('[data-partner-stagger]').forEach((group) => {
    const items = group.querySelectorAll(':scope > [data-partner-reveal]');
    items.forEach((item, index) => {
      item.style.setProperty('--reveal-delay', `${index * 75}ms`);
    });
  });
}

function setupHeroEntrance(root) {
  const heroInner = root.querySelector('.malle-partner__hero-inner');
  if (!heroInner || prefersReducedMotion()) return;

  const items = heroInner.querySelectorAll('[data-partner-reveal]');
  items.forEach((item, index) => {
    item.classList.remove('is-in');
    item.style.setProperty('--reveal-delay', `${index * 85}ms`);
    window.setTimeout(() => item.classList.add('is-in'), 140 + index * 85);
  });
}

function setupPartnerReveals(root) {
  const items = root.querySelectorAll('[data-partner-reveal]');
  if (!items.length) return;

  applyStaggerDelays(root);

  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
  );

  items.forEach((el) => {
    if (el.closest('.malle-partner__hero-inner')) return;
    observer.observe(el);
  });
}

function getHeaderStackBottom() {
  const trustBar = document.querySelector('[data-malle-trust-bar]');
  const header = document.querySelector('#header-component');
  let bottom = 0;

  if (trustBar instanceof HTMLElement) {
    const trustRect = trustBar.getBoundingClientRect();
    if (trustRect.bottom > 0) {
      bottom = Math.max(bottom, trustRect.bottom);
    }
  }

  if (header instanceof HTMLElement) {
    bottom = Math.max(bottom, header.getBoundingClientRect().bottom);
  }

  return Math.max(0, Math.round(bottom));
}

function setupSpotifyBar(root) {
  const bar = root.querySelector('[data-partner-spotify-open]');
  const spacer = root.querySelector('[data-partner-spotify-spacer]');
  if (!(bar instanceof HTMLElement) || !(spacer instanceof HTMLElement)) return;

  const syncBar = () => {
    bar.style.top = `${getHeaderStackBottom()}px`;
    spacer.style.height = `${bar.offsetHeight}px`;
  };

  syncBar();
  window.addEventListener('scroll', syncBar, { passive: true });
  window.addEventListener('resize', syncBar, { passive: true });

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(syncBar);
    observer.observe(bar);

    const trustBar = document.querySelector('[data-malle-trust-bar]');
    const header = document.querySelector('#header-component');
    if (trustBar instanceof HTMLElement) observer.observe(trustBar);
    if (header instanceof HTMLElement) observer.observe(header);
  }
}

function setupSpotifyModal(root) {
  const dialog = root.querySelector('#partner-spotify-modal');
  const openButton = root.querySelector('[data-partner-spotify-open]');
  if (!(dialog instanceof HTMLDialogElement) || !(openButton instanceof HTMLButtonElement)) return;

  const closeButtons = root.querySelectorAll('[data-partner-spotify-close]');

  const openModal = () => {
    if (dialog.open) return;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    if (!dialog.open) return;
    dialog.close();
    document.body.style.overflow = '';
  };

  openButton.addEventListener('click', openModal);

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeModal);
  });

  dialog.addEventListener('click', (event) => {
    const rect = dialog.getBoundingClientRect();
    const isInDialog =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isInDialog) closeModal();
  });

  dialog.addEventListener('close', () => {
    document.body.style.overflow = '';
  });

  dialog.addEventListener('cancel', () => {
    document.body.style.overflow = '';
  });
}

document.querySelectorAll('[data-malle-partner]').forEach((root) => {
  if (!(root instanceof HTMLElement)) return;
  setupHeroEntrance(root);
  setupPartnerReveals(root);
  setupSpotifyBar(root);
  setupSpotifyModal(root);
});
